import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styles from './MonitoringProceduresForm.module.scss';
import RadioButtonGroup from "../../../../components/radioButtonGroup/RadioButtonGroup";
import Dropdown from "../../../../components/dropdown/Dropdown";
import PropTypes from "prop-types";

const MonitoringProceduresForm = forwardRef(({
  formData,
  peripheralIVPlacedList= [],
  totalNumberOfIVsPlacedList = [],
  onSave,
  disabled = false,
}, ref) => {
  const [intraArterialBPMonitoring, setIntraArterialBPMonitoring] = useState(null);
  const [intraArterialBPMonitoringError, setIntraArterialBPMonitoringError] = useState('');
  const [arterialPunctureCatheterInsertion, setArterialPunctureCatheterInsertion] = useState(null);
  const [arterialPunctureCatheterInsertionError, setArterialPunctureCatheterInsertionError] = useState('');
  const [centralVenousPressureMonitoring, setCentralVenousPressureMonitoring] = useState(null);
  const [centralVenousPressureMonitoringError, setCentralVenousPressureMonitoringError] = useState('');
  const [centralVenousCatheterPlacement, setCentralVenousCatheterPlacement] = useState(null);
  const [centralVenousCatheterPlacementError, setCentralVenousCatheterPlacementError] = useState('');
  const [pulmonaryPressureMonitoring, setPulmonaryPressureMonitoring] = useState(null);
  const [pulmonaryPressureMonitoringError, setPulmonaryPressureMonitoringError] = useState('');
  const [pulmonaryArterialCatheterPlacement, setPulmonaryArterialCatheterPlacement] = useState(null);
  const [pulmonaryArterialCatheterPlacementError, setPulmonaryArterialCatheterPlacementError] = useState('');
  const [peripheralIVPlaced, setPeripheralIVPlaced] = useState(null);
  const [peripheralIVPlacedError, setPeripheralIVPlacedError] = useState('');
  const [peripheralIVValue, setPeripheralIVValue] = useState(null);
  const [peripheralIVValueError, setPeripheralIVValueError] = useState('');
  const [totalIVPlaced, setTotalIVPlaced] = useState(null);
  const [totalIVPlacedError, setTotalIVPlacedError] = useState('');

  useEffect(() => {
    setIntraArterialBPMonitoring(formData.intraArterialBPMonitoring);
    setArterialPunctureCatheterInsertion(formData.arterialPunctureCatheterInsertion)
    setCentralVenousPressureMonitoring(formData.centralVenousPressureMonitoring)
    setCentralVenousCatheterPlacement(formData.centralVenousCatheterPlacement)
    setPulmonaryPressureMonitoring(formData.pulmonaryPressureMonitoring)
    setPulmonaryArterialCatheterPlacement(formData.pulmonaryArterialCatheterPlacement)
    setPeripheralIVPlaced(formData.peripheralIVPlaced)
    setPeripheralIVValue(formData.peripheralIVValue || null)
    setTotalIVPlaced(formData.totalIVPlaced || null)
  }, [formData]);

  const validateIntraArterialBPMonitoringError = () => {
    if(intraArterialBPMonitoring === null) {
      return 'Intra-Arterial BP Monitoring is required';
    }
    return '';
  }

  const validateArterialPunctureCatheterInsertionError = () => {
    if(arterialPunctureCatheterInsertion === null) {
      return 'Arterial Puncture/Catheter Insertion is required';
    }
    return '';
  }

  const validateCentralVenousPressureMonitoringError = () => {
    if(centralVenousPressureMonitoring === null) {
      return 'Central Venous Pressure Monitoring is required';
    }
    return '';
  }

  const validateCentralVenousCatheterPlacementError = () => {
    if(centralVenousCatheterPlacement === null) {
      return 'Central Venous Catheter Placement is required';
    }
    return '';
  }

  const validatePulmonaryPressureMonitoringError = () => {
    if(pulmonaryPressureMonitoring === null) {
      return 'Pulmonary Arterial Pressure Monitoring is required';
    }
    return '';
  }

  const validatePulmonaryArterialCatheterPlacementError = () => {
    if(pulmonaryArterialCatheterPlacement === null) {
      return 'Pulmonary Arterial Catheter Placement is required';
    }
    return '';
  }

  const validatePeripheralIVPlacedError = () => {
    if(peripheralIVPlaced === null) {
      return 'Peripheral IV Placed is required';
    }
    return '';
  }

  const validatePeripheralIVValueError = () => {
    if(peripheralIVPlaced && !peripheralIVValue) {
      return 'This field is required';
    }
    return '';
  }

  const validateTotalIVPlacedError = () => {
    if(peripheralIVPlaced && !totalIVPlaced) {
      return 'Total IVs placed is required';
    }
    return '';
  }

  const validateForm = () => {
    const intraArterialBPMonitoringValidationResult = validateIntraArterialBPMonitoringError();
    const arterialPunctureCatheterInsertionValidationResult = validateArterialPunctureCatheterInsertionError();
    const centralVenousPressureMonitoringValidationResult = validateCentralVenousPressureMonitoringError();
    const centralVenousCatheterPlacementValidationResult = validateCentralVenousCatheterPlacementError();
    const pulmonaryPressureMonitoringValidationResult = validatePulmonaryPressureMonitoringError();
    const pulmonaryArterialCatheterPlacementValidationResult = validatePulmonaryArterialCatheterPlacementError();
    const peripheralIVPlacedValidationResult = validatePeripheralIVPlacedError();
    const peripheralIVValueValidationResult = validatePeripheralIVValueError();
    const totalIVPlacedValidationResult = validateTotalIVPlacedError();

    setIntraArterialBPMonitoringError(intraArterialBPMonitoringValidationResult);
    setArterialPunctureCatheterInsertionError(arterialPunctureCatheterInsertionValidationResult);
    setCentralVenousPressureMonitoringError(centralVenousPressureMonitoringValidationResult);
    setCentralVenousCatheterPlacementError(centralVenousCatheterPlacementValidationResult);
    setPulmonaryPressureMonitoringError(pulmonaryPressureMonitoringValidationResult);
    setPulmonaryArterialCatheterPlacementError(pulmonaryArterialCatheterPlacementValidationResult);
    setPeripheralIVPlacedError(peripheralIVPlacedValidationResult);
    setPeripheralIVValueError(peripheralIVValueValidationResult);
    setTotalIVPlacedError(totalIVPlacedValidationResult);

    const errors = [
      intraArterialBPMonitoringValidationResult,
      arterialPunctureCatheterInsertionValidationResult,
      centralVenousPressureMonitoringValidationResult,
      centralVenousCatheterPlacementValidationResult,
      pulmonaryPressureMonitoringValidationResult,
      pulmonaryArterialCatheterPlacementValidationResult,
      peripheralIVPlacedValidationResult,
      peripheralIVValueValidationResult,
      totalIVPlacedValidationResult
    ];
  
    const errorCount = errors.filter(error => error !== '').length;
  
    return errorCount;
  };

  // Expose the function to the parent
  useImperativeHandle(ref, () => ({
    validateForm
  }));

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className={styles.row}>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Intra-Arterial BP Monitoring"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={intraArterialBPMonitoring}
            onChange={(value) => {
              setIntraArterialBPMonitoring(value);
              onSave({
                intraArterialBPMonitoring: value
              })
            }}
            error={intraArterialBPMonitoringError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Arterial Puncture/Catheter Insertion"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={arterialPunctureCatheterInsertion}
            onChange={(value) => {
              setArterialPunctureCatheterInsertion(value)
              onSave({
                arterialPunctureCatheterInsertion: value
              })
            }}
            error={arterialPunctureCatheterInsertionError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Central Venous Pressure Monitoring"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={centralVenousPressureMonitoring}
            onChange={(value) => {
              setCentralVenousPressureMonitoring(value)
              onSave({
                centralVenousPressureMonitoring: value
              })
            }}
            error={centralVenousPressureMonitoringError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Central Venous Catheter Placement"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={centralVenousCatheterPlacement}
            onChange={(value) => {
              setCentralVenousCatheterPlacement(value)
              onSave({
                centralVenousCatheterPlacement: value
              })
            }}
            error={centralVenousCatheterPlacementError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Pulmonary Arterial Pressure Monitoring"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={pulmonaryPressureMonitoring}
            onChange={(value) => {
              setPulmonaryPressureMonitoring(value)
              onSave({
                pulmonaryPressureMonitoring: value
              })
            }}
            error={pulmonaryPressureMonitoringError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Pulmonary Arterial Catheter Placement"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={pulmonaryArterialCatheterPlacement}
            onChange={(value) => {
              setPulmonaryArterialCatheterPlacement(value)
              onSave({
                pulmonaryArterialCatheterPlacement: value
              })
            }}
            error={pulmonaryArterialCatheterPlacementError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Peripheral IV Placed"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={peripheralIVPlaced}
            onChange={(value) => {
              setPeripheralIVPlaced(value)
              onSave(value ? {
                peripheralIVPlaced: value
              } : {
                peripheralIVPlaced: value,
                peripheralIVValue: null,
                totalIVPlaced: null
              })
            }}
            error={peripheralIVPlacedError}
            disabled={disabled}
          />
        </div>
        {peripheralIVPlaced === true && <>
          <div className={styles.column30}>
            <RadioButtonGroup
              label="If, Peripheral IV Placed"
              options={peripheralIVPlacedList}
              selected={peripheralIVValue}
              onChange={(value) => {
                setPeripheralIVValue(value)
                onSave({
                  peripheralIVValue: value
                })
              }}
              error={peripheralIVValueError}
              disabled={disabled}
            />
          </div>
          <div className={styles.column30}>
            <Dropdown
              label="Total IVs placed"
              options={totalNumberOfIVsPlacedList}
              value={totalIVPlaced}
              onChange={(value) => {
                setTotalIVPlaced(value)
                onSave({
                  totalIVPlaced: value
                })
              }}
              error={totalIVPlacedError}
              disabled={disabled}
            />
          </div>
        </>}
      </div>
    </form>
  );
});

MonitoringProceduresForm.displayName = 'MonitoringProceduresForm';

MonitoringProceduresForm.propTypes = {
  formData: PropTypes.object,
  peripheralIVPlacedList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  totalNumberOfIVsPlacedList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default MonitoringProceduresForm;